/**
 * 来場者向け画像URLビルダー
 *
 * notice-images / reward-images / voting-images の3バケットに散っていた
 * `getPublicUrl()` 呼び出しをこの1箇所へ集約する。将来Cloudflare経由の
 * プロキシパスへ切り替える際、このファイルだけを変更すれば済むようにするのが目的。
 *
 * - 配信オリジンは `VITE_PUBLIC_IMAGE_ORIGIN` で差し替え可能（値は文書・ログ・Gitへ書かない）。
 * - 未設定時は現行のSupabase Storage直URL（`getPublicUrl()`の戻り値）へフォールバックする。
 * - 軽量版（サムネイル）のstorage_pathが渡された場合はそちらを優先し、
 *   無ければ原寸のstorage_pathへフォールバックする（有料のオンザフライ変換APIは使わない）。
 */

import { getPublicRuntimeConfig } from '../../shared/config/env';

/** 来場者向け画像を保持する3バケット名 */
export type VisitorImageBucket = 'notice-images' | 'reward-images' | 'voting-images';

/**
 * Storageクライアントの`getPublicUrl`を持つ最小限の型
 * （テストでのモック差し替えを容易にするため、Supabase SDK型へ直接依存しない）
 */
type StorageCapableClient = {
  storage: {
    from: (bucket: string) => {
      getPublicUrl: (path: string) => { data: { publicUrl?: string } | null };
    };
  };
};

/** 画像URL構築オプション */
type BuildVisitorImageUrlOptions = {
  /** 軽量版（サムネイル）のstorage_path。運営が別途アップロードした場合のみ渡す */
  thumbnailStoragePath?: string | null;
};

/**
 * `VITE_PUBLIC_IMAGE_ORIGIN`の設定値を取得する
 * 末尾のスラッシュは比較しやすいよう除去する
 * @returns {string} 設定済みオリジン文字列（未設定時は空文字）
 */
const readImageOriginOverride = (): string => {
  try {
    const configuredOrigin = getPublicRuntimeConfig()?.imageOrigin ?? '';
    return String(configuredOrigin).trim().replace(/\/+$/, '');
  } catch {
    // 必須環境変数未設定などでconfig取得自体が失敗した場合も、
    // 画像URL構築だけは失敗させずフォールバックする
    return '';
  }
};

/**
 * Supabase直URLのオリジンだけを、設定済みの配信オリジンへ差し替える
 * パス・クエリはそのまま維持する
 * @param {string} supabasePublicUrl - `getPublicUrl()`が返した公開URL
 * @param {string} overrideOrigin - 差し替え先オリジン（末尾スラッシュなし）
 * @returns {string} 差し替え後のURL（解析失敗時は元URLをそのまま返す）
 */
const applyOriginOverride = (supabasePublicUrl: string, overrideOrigin: string): string => {
  if (!overrideOrigin) {
    return supabasePublicUrl;
  }

  try {
    const parsedUrl = new URL(supabasePublicUrl);
    return `${overrideOrigin}${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    // 想定外の形式（相対URL等）はSupabase直URLのまま返す
    return supabasePublicUrl;
  }
};

/**
 * 来場者向け画像の公開URLを構築する
 * @param {StorageCapableClient} supabaseClient - Supabaseクライアント（storage.fromを持つもの）
 * @param {VisitorImageBucket} bucket - バケット名
 * @param {string | null | undefined} storagePath - 原寸画像のstorage_path
 * @param {BuildVisitorImageUrlOptions} [options] - 軽量版パス等の追加オプション
 * @returns {string} 公開URL（パス未指定時は空文字）
 */
export const buildVisitorImageUrl = (
  supabaseClient: StorageCapableClient,
  bucket: VisitorImageBucket,
  storagePath: string | null | undefined,
  options?: BuildVisitorImageUrlOptions,
): string => {
  /** 軽量版があれば優先し、無ければ原寸へフォールバックする */
  const preferredPath = String(options?.thumbnailStoragePath ?? '').trim()
    || String(storagePath ?? '').trim();

  if (!preferredPath) {
    return '';
  }

  const { data } = supabaseClient.storage.from(bucket).getPublicUrl(preferredPath);
  const supabasePublicUrl = data?.publicUrl ?? '';

  if (!supabasePublicUrl) {
    return '';
  }

  return applyOriginOverride(supabasePublicUrl, readImageOriginOverride());
};
